/* eslint-disable react/require-default-props */
import React from "react";
import Softphone from "../components/organisms/Softphone";
import {
  CALLING_UNAVAILABLE,
  NO_CALLER_ID_ASSIGNED,
  NO_CALLER_ID_HINT,
} from "../data/callingMessages";
import useClickToCall, { ClickToCallState } from "../hooks/useClickToCall";
import useSoftphone from "../hooks/useSoftphone";

/**
 * Softphone island for the legacy customer list.
 *
 * show_customer1.php is a server-rendered table this owns no part of, so the
 * page contributes the numbers and this contributes the phone. The panel
 * renders in every state on purpose: a rep who cannot call needs to be told
 * which of the two reasons applies, and a rep mid-call needs Hang up and Mute
 * somewhere on the page.
 */
type CustomerCallingProps = {
  /**
   * Rendered inside the customer list's AppLayout: the panel is one of the
   * page's themed cards. Standalone (the default) it brings its own dark slab.
   */
  embedded?: boolean;
};

export default function CustomerCalling({
  embedded = false,
}: CustomerCallingProps) {
  const softphone = useSoftphone();
  const { status } = softphone;

  // Registered, but with no number to call out from. Checked after connecting
  // below, because a token still in flight has no caller id either and saying
  // so for a second on every page load would be a lie.
  const hasNoCallerId = status !== "unavailable" && !softphone.canPlaceCalls;

  let state: ClickToCallState = "busy";
  if (status === "connecting") state = "connecting";
  else if (status === "unavailable" || hasNoCallerId) state = "blocked";
  else if (status === "ready") state = "ready";

  // A missing assignment is the one case an administrator has to fix, so it
  // outranks whatever the device is reporting. Otherwise the hook's own error
  // wins: "unavailable" also covers an expired session and a failed register(),
  // and sending the rep to ask about account setup when reloading the page
  // would fix it wastes their time and somebody else's.
  let blockedReason = NO_CALLER_ID_ASSIGNED;
  if (!hasNoCallerId) blockedReason = softphone.error || CALLING_UNAVAILABLE;

  // The panel already prints softphone.error itself, so the notice below it is
  // only worth rendering when it says something the panel does not.
  const showsReasonBelowPanel = blockedReason !== softphone.error;

  useClickToCall({ state, blockedReason, onCall: softphone.startCall });

  const panel = (
    <Softphone
      status={softphone.status}
      identity={softphone.identity}
      incomingFrom={softphone.incomingFrom}
      error={softphone.error}
      canPlaceCalls={softphone.canPlaceCalls}
      muted={softphone.muted}
      hasBottomMargin
      startCall={softphone.startCall}
      acceptCall={softphone.acceptCall}
      rejectCall={softphone.rejectCall}
      hangUp={softphone.hangUp}
      toggleMute={softphone.toggleMute}
      tone={embedded ? "app" : "dark"}
    />
  );

  return (
    <div className="mb-4">
      {embedded ? (
        panel
      ) : (
        /*
         * The Softphone organism's default tone is the dark calls.php page's:
         * bg-gray-800 at 50% opacity with near-white text. On a white page it
         * composites to mid-gray and its status line and error message are
         * light-on-light, so standalone it gets a dark surface to sit on.
         * Embedded, it uses the "app" tone and follows the page theme.
         */
        <div
          className="rounded-xl bg-gray-900 p-1 text-gray-100"
          data-softphone-surface
        >
          {panel}
        </div>
      )}

      {state === "blocked" && showsReasonBelowPanel ? (
        <p
          className="mt-2 text-sm text-amber-700 dark:text-amber-300"
          role="status"
        >
          {blockedReason}
          {hasNoCallerId ? ` ${NO_CALLER_ID_HINT}` : ""}
        </p>
      ) : null}
    </div>
  );
}
